<template>
  <div class="shortcut-bundles-tab">
    <!-- Shortcut Bundles Grid -->
    <div class="shortcut-grid">
      <div
        v-for="shortcut in shortcutList"
        :key="shortcut.id"
        :class="[
          'shortcut-card-container',
          { 'is-selected': shortcut.isSelect },
          { 'not-allowed-bg not-allowed-font-color' : !isAllowed(shortcut) },
        ]"
        @click="handleShortcutClick(shortcut)"
      >
        <div class="card-head">
          <div class="code-section">
            <img
              :src="getCheckboxImage(shortcut.isSelect)"
              alt="checkbox"
              :class="['checkbox', { 'not-allowed-checkbox' : !isAllowed(shortcut) }]"
            >
            <p class="panel-code">{{ shortcut.id }}</p>
          </div>
          <p v-if="shortcut.price !== undefined && shortcut.price !== null" class="price">{{ formatPrice(shortcut.price) }}</p>
        </div>

        <div class="card-body">
          <p :class="['card-title', { 'not-allowed-font-color': !isAllowed(shortcut) }]">
            {{ shortcut.shortcutName }}
          </p>

          <!-- Sample type tags -->
          <div v-if="getSampleTypeTags(shortcut).length > 0" class="tag-list">
            <TestSampleTypeTagNew
              v-for="tag in getSampleTypeTags(shortcut)"
              :key="tag"
              :type="tag"
            />
          </div>

        </div>
      </div>

      <!-- Empty state when no shortcuts -->
      <div v-if="shortcutList.length === 0 && !shortcutPanelLoading" class="empty-state">
        <p class="empty-text">No shortcut bundles available. Create shortcuts in the order page to see them here.</p>
      </div>
    </div>

    <!-- Item Not Allowed Dialog -->
    <ItemNotAllowedDialog
      v-if="showNotAllowedDialog"
      :is-show-dialog="showNotAllowedDialog"
      :item-category="'shortcut'"
      :item-name="currentShortcut?.shortcutName || ''"
      :dialog-type="notAllowedDialogType"
      @close="showNotAllowedDialog = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import TestSampleTypeTagNew from '@/views/va-orderTest/components/TestSampleTypeTagNew.vue';
import ItemNotAllowedDialog from '@/views/va-orderTest/order-page/ItemNotAllowedDialog.vue';
import { isItemAllowedForCurrentMenu, isItemAllowedForPatientAge } from '@/views/va-orderTest/order-page/custom-panel/customPanelCard';
import { getSampleTypeSetsNew, getSampleTypeTagInfoFromSetsNew } from '@/utils/va-order-test/sampleTypes.js';

export default {
  name: "ShortcutBundlesTab",
  components: {
    TestSampleTypeTagNew,
    ItemNotAllowedDialog,
  },
  emits: ['selection-change'],
  data() {
    return {
      showNotAllowedDialog: false,
      currentShortcut: null,
      notAllowedDialogType: 'notAllowedForMenu',
      prefix: {},
    };
  },
  computed: {
    ...mapState('orderTest', ['isDbsVersion', 'shortcutPanelLoading']),
    ...mapState('orderTest/panels', {
      customPanelList: state => state.customPanelList,
      discountPanelList: state => state.discountPanelList,
      serumDiscountPanelList: state => state.serumDiscountPanelList,
      dbsDiscountPanelList: state => state.dbsDiscountPanelList,
    }),
    ...mapState('orderTest/tests', [
      'vaRootNodeList',
      'vwRootNodeList',
      'vaBfsList',
      'vwBfsList',
      'regenereIdList',
      'nutriprozIdList',
      'vwCategories',
    ]),
    ...mapState('orderTest/favorites', ['shortcutList', 'selectedShortcutIdList']),
    ...mapGetters('orderTest', ['orderTestSupportObj', 'selectedItemList', 'selectedItemsPrice']),
    ...mapGetters('orderTest/tests', [
      'testList',
      'bfsTestList',
      'vwTestList',
      'serumCategorizedVwTests',
      'dbsCategorizedVwTests',
      'regenereNodeList',
      'nutriprozNodeList',
    ]),
    ...mapGetters('orderTest/panels', ['bundleList']),
    ...mapGetters('orderTest/order', ['patientAge']),

    displayItemData() {
      return {
        bundleList: this.bundleList,
        vaRootNodeList: this.vaRootNodeList,
        vwRootNodeList: this.vwRootNodeList,
        regenereIdList: this.regenereIdList,
        nutriprozIdList: this.nutriprozIdList,
        vaBfsList: this.vaBfsList,
        vwBfsList: this.vwBfsList,
        bfsTestList: this.bfsTestList,
        testList: this.testList,
        vwTestList: this.vwTestList,
        serumCategorizedVwTests: this.serumCategorizedVwTests,
        dbsCategorizedVwTests: this.dbsCategorizedVwTests,
        regenereNodeList: this.regenereNodeList,
        nutriprozNodeList: this.nutriprozNodeList,
        discountPanelList: this.discountPanelList,
        serumDiscountPanelList: this.serumDiscountPanelList,
        dbsDiscountPanelList: this.dbsDiscountPanelList,
      };
    },
  },
  mounted() {
    this.prefix = this.getPrefixPicture();
    // Load shortcut list if not already loaded
    if (this.shortcutList.length === 0) {
      this.$store.dispatch('orderTest/favorites/setShortcutList');
    }
  },
  methods: {
    isAllowed(shortcut) {
      return this.isItemAllowedForCurrentMenu(shortcut) && this.isAllowedForPatientAge(shortcut);
    },

    isItemAllowedForCurrentMenu(shortcut) {
      return isItemAllowedForCurrentMenu(this.isDbsVersion, shortcut, 'isDbs');
    },

    isAllowedForPatientAge(shortcut) {
      return isItemAllowedForPatientAge(shortcut, this.orderTestSupportObj, this.patientAge);
    },

    getSampleTypeTags(shortcut) {
      const sampleSets = getSampleTypeSetsNew(shortcut);
      return getSampleTypeTagInfoFromSetsNew(sampleSets).keys || [];
    },

    getCheckboxImage(isSelected) {
      return isSelected
        ? require('@/assets/orderTest/Checkbox(all).svg')
        : require('@/assets/orderTest/check_box_outline_blank_24px.svg');
    },

    getPrefixPicture() {
      return {
        unCheck: require('@/assets/orderTest/check_box_outline_blank_24px.svg'),
        allCheck: require('@/assets/orderTest/Checkbox(all).svg'),
      };
    },

    formatPrice(price) {
      if (price === undefined || price === null) return '';
      const symbol = this.$store.state.currency?.symbol || '$';
      return `${symbol}${Number(price).toFixed(2)}`;
    },

    handleShortcutClick(shortcut) {
      if (!this.isAllowed(shortcut)) {
        this.currentShortcut = shortcut;
        this.notAllowedDialogType = !this.isAllowedForPatientAge(shortcut) ? 'notAllowedForPatientAge' : 'notAllowedForMenu';
        this.showNotAllowedDialog = true;
        return;
      }

      if (shortcut.isSelect) {
        this.$store.dispatch('orderTest/removeSelectedShortcut', shortcut);
      } else {
        // Check if shortcut has xChooseY panels that need selection
        if (shortcut.xChooseYIdList && shortcut.xChooseYIdList.length > 0) {
          const xChooseYPanelsNeedingSelection = shortcut.xChooseYIdList.filter(
            xChooseY => !xChooseY.selectedZoomerIdList || xChooseY.selectedZoomerIdList.length === 0
          );
          if (xChooseYPanelsNeedingSelection.length > 0) {
            // For now, just select the shortcut
            // In a full implementation, you'd show a dialog for XChooseY selection
          }
        }
        this.$store.dispatch('orderTest/addSelectedShortcut', shortcut);
      }

      // Emit selection change
      this.$emit('selection-change', this.selectedItemList, this.selectedItemsPrice);
    },
  },
};
</script>

<style scoped>
.shortcut-bundles-tab {
  width: 100%;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.shortcut-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  padding: 4px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  align-content: start;
}

.shortcut-card-container {
  min-height: 98px;
  background: #FFF;
  box-shadow: 0 4px 12px 0 rgba(26, 32, 44, 0.06);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.shortcut-card-container:hover {
  background: #FFF;
  box-shadow: 0 4px 16px 0 rgba(26, 32, 44, 0.1);
}

.is-selected {
  border-color: #004879;
  background: #F9FBFD;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.code-section {
  display: flex;
  align-items: center;
  gap: 7px;
}

.panel-code {
  color: #000;
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.2px;
  margin: 0;
}

.card-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox {
  height: 22px;
  width: 22px;
  min-width: 22px;
  filter: brightness(0) saturate(100%) invert(8%) sepia(100%) saturate(3500%) hue-rotate(190deg);
}

.not-allowed-checkbox {
  opacity: 0.4;
}

.card-title {
  letter-spacing: 0.1px;
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  margin: 0;
}

.not-allowed-bg {
  background: #F2F2F2;
}

.not-allowed-font-color {
  color: #979797;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.shortcut-tag-wrapper {
  display: flex;
  margin-top: 4px;
}

.shortcut-tag {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px;
  gap: 4px;
  height: 24px;
  border-radius: 12px;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 0.4px;
  color: #000000;
}

.clinic-tag {
  background-color: #B4E5EA;
}

.provider-tag {
  background-color: #FFD189;
}

.allowed-tag {
  opacity: 1;
}

.not-allowed-tag {
  background-color: #979797;
  color: #F2F2F2;
  opacity: 0.8;
}

.tag-icon {
  font-size: 14px;
}

.price {
  color: #20A7C1;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  white-space: nowrap;
  margin: 0;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 40px 20px;
  text-align: center;
  background: #F9FBFD;
  border-radius: 8px;
  border: 1px dashed #004879;
}

.empty-text {
  font-size: 14px;
  color: #004879;
  margin: 0;
}
</style>
